import Cookies from "js-cookie";
import { Fragment } from "react";

import type { UserProfile } from "@/types/user";

import axios from "@/api/axios";
import { usePrivateAxios } from "@/api/use-private-axios";
import { AppSidebar } from "@/components/dashboard/app-sidebar";
import { NotificationPopover } from "@/components/dashboard/notification-popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import {
	SidebarInset,
	SidebarProvider,
	SidebarTrigger
} from "@/components/ui/sidebar";
import { BUYER_SIDEBAR_DATA } from "@/lib/sidebar-constants";
import { formatKebabCase } from "@/lib/utils";
import useAuthStore from "@/store/auth";
import { usePrefetchQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	Outlet,
	redirect,
	useLocation
} from "@tanstack/react-router";

export async function refreshToken() {
	const { user, tokens } = useAuthStore.getState();

	if (!user) {
		throw new Error("User cannot be null");
	}

	if (!tokens?.refreshToken) {
		throw new Error("Refresh token is not available");
	}

	try {
		const response = await axios.post(
			"auth/refresh_token",
			{
				userId: user.id,
				oldRefreshToken: tokens.refreshToken
			},
			{
				headers: {
					"Content-Type": "application/json"
				}
			}
		);

		const { access_token, refreshToken: refresh_token } = response.data.data;

		return { access_token, refresh_token };
	}
	catch (error) {
		// @ts-expect-error error type is unknown
		if (error.response?.data?.message === "Token is already invalid") {
			throw new Error("Token is already invalid");
		}
		console.error("Error refreshing token:", error);
		throw error;
	}
}

export const Route = createFileRoute("/buyer")({
	loader: async () => {
		const { user, setTokens } = useAuthStore.getState();
		try {
			await axios.get<{ data: UserProfile }>("/auth/user_profile");
		}
		catch (error: unknown) {
			// @ts-expect-error error type is unknown
			if (error.response?.status >= 400) {
				const { access_token, refresh_token } = await refreshToken();
				if (access_token && refresh_token) {
					setTokens({
						accessToken: access_token,
						refreshToken: refresh_token
					});
				}
			}
		}

		if (!user) {
			throw redirect({
				to: "/sign-in"
			});
		}
		if (user.userType !== "BUYER") {
			throw redirect({
				to: "/vendor/home"
			});
		}
		if (location.pathname === "/buyer" || location.pathname === "/buyer/") {
			throw redirect({
				to: "/buyer/home"
			});
		}
	},

	component: RouteComponent
});

function RouteComponent() {
	const location = useLocation();
	const user = useAuthStore(state => state.user);
	const userInitials = `${user?.firstName.charAt(0)}${user?.lastName.charAt(
		0
	)}`;
	let breadcrumbRoute: string = "";

	const privateAxios = usePrivateAxios();
	usePrefetchQuery({
		queryKey: ["order-disputes"],
		queryFn: async () => {
			return await privateAxios.get("/order/list_desputes-for-orders");
		}
	});

	return (
		<section>
			<SidebarProvider defaultOpen={Cookies.get("sidebar:state") === "true"}>
				<AppSidebar data={BUYER_SIDEBAR_DATA} />
				<SidebarInset className="bg-gray-100">
					<header className="flex h-16 shrink-0 items-center justify-between gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
						<div className="flex items-center  gap-2 px-4">
							<SidebarTrigger className="-ml-1" />
							<Separator orientation="vertical" className="mr-2 h-4" />
							<Breadcrumb>
								<BreadcrumbList>
									{location.pathname
										.split("/")
										.filter(path => !!path)
										.map((path, idx) => {
											const len = location.pathname.length;
											breadcrumbRoute = breadcrumbRoute.concat(`/${path}`);
											const isActive
													= location.pathname.endsWith(breadcrumbRoute, len)
													|| location.pathname.endsWith(breadcrumbRoute, len - 1);
											const formattedpath = formatKebabCase(path);
											return (
												idx > 0 && (
													<Fragment key={breadcrumbRoute}>
														{idx > 1 && (
															<BreadcrumbSeparator className="hidden md:block" />
														)}
														{isActive
															? (
																	<BreadcrumbItem>
																		<BreadcrumbPage className="capitalize">
																			{formattedpath}
																		</BreadcrumbPage>
																	</BreadcrumbItem>
																)
															: (
																	<BreadcrumbItem className="hidden md:block">
																		<BreadcrumbLink asChild>
																			<Link
																				to={breadcrumbRoute}
																				className="capitalize"
																			>
																				{formattedpath}
																			</Link>
																		</BreadcrumbLink>
																	</BreadcrumbItem>
																)}
													</Fragment>
												)
											);
										})}
								</BreadcrumbList>
							</Breadcrumb>
						</div>
						<NotificationPopover />
						<Avatar className="size-10 rounded-full mx-2 border-2 border-primary">
							<AvatarImage src="/change" alt={user?.fullName} />
							<AvatarFallback className="rounded-lg">
								{userInitials}
							</AvatarFallback>
						</Avatar>
					</header>
					<div className="flex flex-1 flex-col gap-4 p-4 pt-0">
						<Outlet />
					</div>
				</SidebarInset>
			</SidebarProvider>
		</section>
	);
}
